import {
  DeleteOutlined,
  EllipsisOutlined,
  FilePdfOutlined,
  LockOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Button, Tag, Tooltip } from 'antd';
import { ColumnType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import React from 'react';

import ActionPopoverButton from '../../components/ActionPopoverButton';
import { CertificateActionAttendee } from '../hooks/useAttendeeCertificateActions';
import {
  CertificateResultStatus,
  OpenClassCertificateType,
} from '../types/OpenClass';
import { usesUploadedCertificate } from '../utils/certificateEligibility';
import { certificateTypeLabel } from '../utils/certificateTypeLabel';
import { RowActionsParams, attendeeRowActionElements } from './AttendeeActions';
import CertificatePdfUpload from './CertificatePdfUpload';

// The columns both attendee lists render the same way. What differs between a session and
// a whole class is passed in.
type NamedAttendee = { email?: string | null; name: string };

type CompanyAttendee = NamedAttendee & {
  clientCompanyId?: number;
  clientCompanyName?: string;
};

type CertificateAttendee = CertificateActionAttendee &
  NamedAttendee & {
    certificateResultStatus: CertificateResultStatus;
    certificateSentTo?: string | null;
    certificateType: OpenClassCertificateType;
  };

export const attendeeNameColumn = <T extends NamedAttendee>(
  t: TFunction,
  isAbsent: (attendee: T) => boolean,
): ColumnType<T> => ({
  title: t('components.OpenClassAttendeeActions.columnAttendee'),
  key: 'attendee',
  render: (_, attendee) => (
    <div>
      <div
        className={
          isAbsent(attendee) ? 'font-medium text-warningRed' : 'font-medium'
        }>
        {attendee.name}
      </div>
      <div className="text-blueGrayDark">{attendee.email || '–'}</div>
    </div>
  ),
});

export const attendeeCompanyColumn = <T extends CompanyAttendee>(
  t: TFunction,
): ColumnType<T> => ({
  title: t('components.OpenClassAttendeeActions.columnCompany'),
  key: 'company',
  render: (_, attendee) =>
    attendee.clientCompanyName ||
    t('components.OpenClassAttendeeActions.privateParticipant'),
});

// A registration holds one of the class's types, the rest are greyed to show what wasn't
// chosen.
export const attendeeCertificatesColumn = <
  T extends { certificateType: OpenClassCertificateType },
>(
  t: TFunction,
  certificateTypes: OpenClassCertificateType[],
): ColumnType<T> => ({
  title: t('components.OpenClassAttendeeActions.columnCertificates'),
  key: 'certificates',
  render: (_, attendee) => (
    <div className="flex flex-row gap-3">
      {certificateTypes.map((certificateType) => (
        <Tag
          key={certificateType}
          className={
            certificateType === attendee.certificateType
              ? 'm-0 rounded-full border-0 bg-safeLifeMedium text-white'
              : 'm-0 rounded-full border-0 bg-blueGrayLight text-blueGrayDark'
          }>
          {certificateTypeLabel(certificateType)}
        </Tag>
      ))}
    </div>
  ),
});

export const attendeeCertificateSentColumn = <T extends CertificateAttendee>({
  t,
}: {
  t: TFunction;
}): ColumnType<T> => ({
  title: t('components.OpenClassAttendeeActions.columnCertificateEmailSent'),
  key: 'certificateEmailSent',
  render: (_, attendee) => {
    if (attendee.certificateSentAt) {
      const sentAt = dayjs(attendee.certificateSentAt).format(
        'YYYY-MM-DD HH:mm',
      );
      return attendee.certificateSentTo &&
        attendee.certificateSentTo !== attendee.email ? (
        <Tooltip
          title={t('components.OpenClassAttendeeActions.sentToContactPerson', {
            email: attendee.certificateSentTo,
          })}>
          <span className="underline decoration-dotted">{sentAt}</span>
        </Tooltip>
      ) : (
        sentAt
      );
    }
    if (attendee.certificateIssued) {
      return (
        <Tooltip
          title={t(
            'components.OpenClassAttendeeActions.certificateIssuedNotEmailed',
          )}>
          <Tag className="m-0 rounded-full border-0 bg-blueGrayLight text-blueGrayDark">
            {t('components.OpenClassAttendeeActions.certificateIssuedShort')}
          </Tag>
        </Tooltip>
      );
    }
    return '–';
  },
});

type BesaccCertificateAttendee = CertificateAttendee & {
  besaccCertificateFileName?: string | null;
};

export const attendeeBesaccCertificateColumn = <
  T extends BesaccCertificateAttendee,
>({
  canUpdate,
  saving,
  t,
  onPreview,
  onRemove,
  onUpload,
}: {
  canUpdate: boolean;
  saving: boolean;
  t: TFunction;
  onPreview: (attendee: T) => void;
  onRemove: (attendee: T) => void;
  onUpload: (attendee: T, file: File) => void;
}): ColumnType<T> => ({
  title: t('components.OpenClassAttendeeActions.columnCertificateFile'),
  key: 'certificateFile',
  width: 150,
  render: (_, attendee) => {
    if (!usesUploadedCertificate(attendee.certificateType)) return '–';
    const fileName = attendee.besaccCertificateFileName;
    const locked = Boolean(attendee.certificateSentAt);
    if (!fileName) {
      // Only a passed attendee can ever be issued the PDF, so nobody else gets to attach one
      return canUpdate && attendee.certificateResultStatus === 'passed' ? (
        <CertificatePdfUpload onAccepted={(file) => onUpload(attendee, file)}>
          <Button
            className="h-auto p-0"
            disabled={saving}
            icon={<UploadOutlined />}
            type="link">
            {t('components.BesaccCertificatePreview.upload')}
          </Button>
        </CertificatePdfUpload>
      ) : (
        '–'
      );
    }
    return (
      <div className="flex items-center gap-1">
        <Tooltip title={fileName}>
          <Button
            className="h-auto p-0"
            icon={<FilePdfOutlined />}
            type="link"
            onClick={() => onPreview(attendee)}>
            {t('components.BesaccCertificatePreview.viewPdf')}
          </Button>
        </Tooltip>
        {locked ? (
          <Tooltip title={t('components.BesaccCertificatePreview.locked')}>
            <LockOutlined className="text-grayMedium" />
          </Tooltip>
        ) : canUpdate ? (
          <Tooltip title={t('components.BesaccCertificatePreview.remove')}>
            <Button
              aria-label={t('components.BesaccCertificatePreview.remove')}
              danger
              disabled={saving}
              icon={<DeleteOutlined />}
              size="small"
              type="text"
              onClick={() => onRemove(attendee)}
            />
          </Tooltip>
        ) : null}
      </div>
    );
  },
});

export const attendeeActionsColumn = <T extends CertificateActionAttendee>({
  canUpdate,
  ...rowActions
}: Omit<RowActionsParams<T>, 'attendee'> & {
  canUpdate: boolean;
}): ColumnType<T> => ({
  title: '',
  key: 'actions',
  align: 'right',
  width: 56,
  render: (_, attendee) =>
    canUpdate && (
      <ActionPopoverButton
        absolutePlacement={false}
        actionPopoverElements={attendeeRowActionElements({
          attendee,
          ...rowActions,
        })}
        // A real button keeps the actions keyboard reachable, the popover's default
        // trigger is a plain div.
        icon={
          <Button
            aria-label={rowActions.t(
              'components.OpenClassAttendeeActions.rowActions',
            )}
            icon={<EllipsisOutlined />}
            type="text"
          />
        }
        placement="bottomRight"
      />
    ),
});
